import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { BLOGS, getBlogBySlug, type Blog } from "@/lib/blogs";
import SizingGuideArticle from "@/components/site/SizingGuideArticle";
import { articleJsonLd, breadcrumbJsonLd, faqPageJsonLd, howToJsonLd, SITE_URL, type FAQItem, type HowToStep } from "@/lib/seo";
import { BackButton } from "@/components/site/BackButton";

// FAQ data for the mega FAQ article (used for schema injection)
const FAQ_SCHEMA_SLUGS: Record<string, FAQItem[]> = {
  "leather-faq": [
    { question: "Is leather a by-product of the food industry?", answer: "Yes. Roughly 99 percent of leather is produced from hides that are a by-product of the meat and dairy industries. The hide accounts for only 5 to 10 percent of the total value of a slaughtered animal." },
    { question: "Is leather sustainable?", answer: "When responsibly sourced and tanned at LWG-certified facilities, leather is one of the more sustainable materials available. It diverts waste from landfill, biodegrades at end of life, and lasts for decades — reducing consumption cycles." },
    { question: "What are the different grades of leather?", answer: "Leather is graded into four tiers: Full-Grain (highest quality, natural grain intact), Top-Grain (lightly sanded for uniformity), Genuine Leather (lower layers of the hide, heavily processed), and Bonded Leather (shredded scraps glued together — the lowest tier)." },
    { question: "What is chrome-tanned leather?", answer: "Chrome tanning uses chromium(III) salts to stabilise hides. It accounts for 80-85% of global production, is fast (completed in one day), and produces soft, water-resistant leather. It is safe when performed in regulated facilities." },
    { question: "What is vegetable-tanned leather?", answer: "Vegetable tanning uses natural tannins from tree bark and plant matter. It takes 2-6 weeks but produces leather with exceptional character that develops a deep, warm patina over time." },
    { question: "What is the difference between real leather and vegan leather?", answer: "Real leather is tanned animal hide — natural, breathable, and lasting 20-50 years. Vegan leather is typically polyurethane or PVC (petroleum-derived plastics) lasting 2-5 years, or newer plant-based alternatives still in early development." },
    { question: "How long does leather last?", answer: "Full-grain leather can last 20-30+ years with proper care. Top-grain lasts 10-20 years. Genuine leather lasts 2-5 years. Bonded leather often peels within 1-3 years." },
    { question: "How do I care for my leather jacket?", answer: "Hang on a padded hanger after wearing. Wipe weekly with a dry microfibre cloth. Condition every 3-6 months with a pH-balanced leather conditioner. Store in a breathable cotton garment bag away from sunlight and heat." },
    { question: "Is leather waterproof?", answer: "Leather is naturally water-resistant but not waterproof. Light rain is fine for conditioned leather. Sustained exposure causes stiffening and cracking. Water resistance can be enhanced with leather-specific sprays or wax treatments." },
    { question: "How do I fix scratches on leather?", answer: "Light scuffs can be rubbed out with your fingertip using body heat and natural oils. Moderate scratches respond to colour-matched leather balm. Deep cuts require leather filler, dye, and sealant — or professional restoration." },
    { question: "Can leather be recycled?", answer: "Yes. Scraps can be shredded into fibre and combined with binders to create reconstituted leather board. Finished products can be upcycled into new items. Vegetable-tanned leather is fully biodegradable." },
    { question: "How do I spot high-quality leather?", answer: "Use five sensory tests: Touch (should wrinkle like skin), Smell (earthy, sweet aroma), Edge (rough, fibrous edges), Grain (irregular natural patterns), and Weight (dense, substantial feel)." },
    { question: "What does 'genuine leather' actually mean?", answer: "Despite sounding like a quality guarantee, 'genuine leather' denotes the second-lowest grade — made from lower hide layers after the top grain is removed. It is heavily processed and prone to cracking within 2-5 years." },
    { question: "Does leather scratch easily?", answer: "It depends on the finish. Aniline (uncoated) full-grain shows scratches more readily but develops patina that hides them over time. Pigmented leather is more scratch-resistant but has a less natural feel." },
    { question: "Why does leather develop a patina?", answer: "Patina results from gradual oxidation of natural oils, friction from wear, and UV exposure. It creates a rich, lustrous sheen unique to each piece — most pronounced on full-grain vegetable-tanned leather." },
    { question: "What leather is best for jackets?", answer: "Cowhide (most durable), lambskin (softest, most luxurious drape), goatskin (balanced durability and lightness), and buffalo (heaviest, most rugged). The choice depends on your priority — ruggedness vs comfort vs weight." },
    { question: "Can I wear my leather jacket in the rain?", answer: "Light rain is fine for conditioned leather. In heavy rain, get indoors, blot excess water, and air-dry on a wide hanger away from heat. Condition after drying to replenish displaced oils." },
    { question: "How should I store my leather jacket in summer?", answer: "Clean and condition first. Hang on a padded hanger inside a breathable cotton garment bag. Store in a cool, dark, dry place. Never fold — folds become permanent creases. Add silica gel in humid climates." },
  ],
};

// HowTo schema for care articles
const HOWTO_SCHEMA_SLUGS: Record<string, { name: string; description: string; totalTime: string; steps: HowToStep[] }> = {
  "how-to-clean-and-condition-leather": {
    name: "How to Clean and Condition Leather",
    description: "A step-by-step guide to cleaning and conditioning leather jackets, bags, and accessories to extend their lifespan.",
    totalTime: "PT45M",
    steps: [
      { name: "Gather supplies", text: "Collect a soft microfibre cloth, pH-balanced leather cleaner, leather conditioner, and distilled water. For suede, use a suede brush and eraser instead." },
      { name: "Dust and surface clean", text: "Wipe the entire surface with a dry microfibre cloth, paying attention to seams and folds where grit accumulates." },
      { name: "Clean with leather cleaner", text: "Dampen a soft cloth with leather cleaner and work in small sections using gentle circular motions. Wipe residue with a clean, damp cloth." },
      { name: "Condition the leather", text: "Apply conditioner to a clean cloth using circular motions. Use thin, even coats. Allow to absorb for 30-60 minutes, then buff with a dry cloth." },
      { name: "Protect and waterproof", text: "Apply a leather-specific water-repellent spray, holding 15-20 cm from the surface. Allow to dry completely before wearing." },
      { name: "Store properly", text: "Hang on a wide, padded hanger inside a breathable cotton garment bag. Keep away from sunlight and heat sources." },
    ],
  },
  "how-to-clean-and-maintain-different-types-of-leather": {
    name: "How to Clean and Maintain Every Type of Leather",
    description: "A complete type-by-type routine for cleaning, conditioning, drying and storing every major kind of leather: full-grain, top-grain, nappa, lambskin, suede, nubuck, patent, oil-pull-up, exotic and shearling.",
    totalTime: "PT1H",
    steps: [
      { name: "Identify the leather type", text: "Check the care label or use tactile cues — pores and patina mean full-grain, smooth uniform finish means top-grain, soft pebbled means nappa, fuzzy nap means suede, fine velvet nap means nubuck, glossy plastic-like finish means patent." },
      { name: "Test any product on a hidden patch", text: "Apply your cleaner, conditioner or protector to an inside hem or under-collar area and wait 24 hours to check for darkening or stiffening before treating the visible piece." },
      { name: "Dust before cleaning", text: "Wipe smooth leathers with a dry microfibre cloth. Brush suede, nubuck and shearling gently in one direction with a dedicated nap brush." },
      { name: "Clean according to type", text: "Smooth leathers: barely-damp distilled water cloth, or a pH-balanced leather cleaner for deeper grime. Suede and nubuck: dry suede brush plus a suede eraser for spots. Patent: damp cloth with a drop of mild soap. Never use household soap, olive oil or kitchen oils." },
      { name: "Dry slowly at room temperature", text: "Hang on a wide padded hanger away from radiators, hairdryers and direct sunlight. Heat shrinks and cracks every type of leather." },
      { name: "Condition smooth leathers only", text: "Once fully dry, apply a thin even layer of conditioner appropriate to the type with a soft cloth using small circular motions. Let absorb at least 30 minutes, then buff. Never condition suede, nubuck or shearling — it mats the nap permanently." },
      { name: "Protect with a leather-safe spray", text: "Apply a colourless silicone-free water-repellent (leather-formulated for smooth leathers, suede-formulated for napped leathers) from 20–25 cm in two thin coats, letting each dry between applications." },
      { name: "Store correctly", text: "Use a wide padded hanger and a breathable cotton garment bag. Stuff bags with acid-free tissue. Keep at 18–22°C and 40–50% humidity, away from sunlight, plastic bags and damp basements." },
    ],
  },
};

export const Route = createFileRoute("/journal/$slug")({
  loader: ({ params }) => {
    const blog = getBlogBySlug(params.slug);
    if (!blog) throw notFound();
    return { blog };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Journal — KAKAIYS" }] };
    const { blog } = loaderData;
    const description = blog.metaDescription ?? blog.excerpt;
    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: articleJsonLd({
          slug: blog.slug,
          title: blog.title,
          description,
          image: blog.hero,
          datePublished: blog.publishedOn,
          author: blog.author,
          keywords: blog.keywords,
        }),
      },
      {
        type: "application/ld+json",
        children: breadcrumbJsonLd([
          { name: "Home", url: "/" },
          { name: "Journal", url: "/journal" },
          { name: blog.title, url: `/journal/${blog.slug}` },
        ]),
      },
    ];
    // Inject FAQPage schema for FAQ articles
    if (FAQ_SCHEMA_SLUGS[blog.slug]) {
      scripts.push({
        type: "application/ld+json",
        children: faqPageJsonLd(FAQ_SCHEMA_SLUGS[blog.slug]),
      });
    }
    // Inject HowTo schema for care/how-to articles
    if (HOWTO_SCHEMA_SLUGS[blog.slug]) {
      const ht = HOWTO_SCHEMA_SLUGS[blog.slug];
      scripts.push({
        type: "application/ld+json",
        children: howToJsonLd({ ...ht, image: blog.hero }),
      });
    }
    const SUFFIX = " | KAKAIYS";
    const titleTag = blog.title.length + SUFFIX.length > 60 ? blog.title : `${blog.title}${SUFFIX}`;
    return {
      meta: [
        { title: titleTag },
        { name: "description", content: description },
        ...(blog.keywords && blog.keywords.length > 0
          ? [{ name: "keywords", content: blog.keywords.join(", ") }]
          : []),
        { name: "author", content: blog.author ?? "KAKAIYS" },
        { property: "article:published_time", content: blog.publishedOn },
        { property: "og:title", content: blog.title },
        { property: "og:description", content: description },
        { property: "og:image", content: blog.hero },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `${SITE_URL}/journal/${blog.slug}` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: blog.title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: blog.hero },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/journal/${blog.slug}` }],
      scripts,
    };
  },
  notFoundComponent: () => (
    <div className="container-luxe pt-40 pb-32 text-center">
      <BackButton fallback="/journal" className="mb-8" />
      <div className="eyebrow mb-4">Not Found</div>
      <h1 className="font-serif text-4xl">Essay not found.</h1>
      <Link to="/journal" className="mt-8 inline-flex text-xs uppercase tracking-[0.24em] text-gold border-b border-gold pb-1">
        Return to the Journal
      </Link>
    </div>
  ),
  component: BlogDetailPage,
});

function BlogDetailPage() {
  const { blog } = Route.useLoaderData() as { blog: Blog };
  const others = BLOGS.filter((b) => b.slug !== blog.slug).slice(0, 3);

  // Bespoke long-form layout for the sizing guide
  if (blog.slug === "how-to-choose-the-right-size") {
    return (
      <article>
        <SizingGuideArticle hero={blog.hero} />
        <div className="container-luxe pt-8">
          <BackButton fallback="/journal" />
        </div>
        {/* MORE READS */}
        <section className="container-luxe py-24 md:py-32 border-t border-border">
          <div className="eyebrow mb-3 text-gold">Continue Reading</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-12">More from the journal.</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {others.map((b) => (
              <Link
                key={b.slug}
                to="/journal/$slug"
                params={{ slug: b.slug }}
                preload="intent"
                className="group block"
              >
                <div className="relative aspect-[4/5] overflow-hidden img-frame mb-5">
                  <img
                    src={b.hero}
                    alt={b.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="eyebrow text-gold mb-2">{b.category}</div>
                <h3 className="font-serif text-xl leading-tight group-hover:text-gold transition-colors">
                  {b.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      </article>
    );
  }

  return (
    <article>
      {/* HERO */}
      <section className="relative h-[80svh] min-h-[520px] w-full overflow-hidden bg-ink">
        <img
          src={blog.hero}
          alt={blog.title}
          className="absolute inset-0 h-full w-full object-cover opacity-80 ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/30 to-ink/90" />
        <div className="relative z-10 flex h-full items-end pb-16 md:pb-24">
          <div className="container-luxe text-bone max-w-4xl">
            <BackButton fallback="/journal" className="mb-8" />
            <div className="eyebrow text-gold">
              {blog.category} · {blog.readMinutes} min read · {blog.publishedOn}
            </div>
            <h1 className="mt-5 font-serif text-4xl md:text-7xl leading-[1.05] text-slate-50 bg-inherit">{blog.title}</h1>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="container-luxe py-16 md:py-24 max-w-3xl">
        <p className="font-serif text-xl md:text-2xl leading-snug text-foreground italic">
          {blog.intro}
        </p>
      </section>

      {/* SECTIONS */}
      <div className="pb-24 md:pb-32">
        {blog.sections.map((s, i) => (
          <section key={i} className="container-luxe max-w-3xl py-10 md:py-14">
            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-6">{s.heading}</h2>
            <div className="space-y-5 text-muted-foreground leading-relaxed text-base md:text-lg">
              {s.body.map((p, k) => (
                <p key={k}>{p}</p>
              ))}
            </div>
            {s.image && (
              <figure className="mt-10">
                <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden img-frame">
                  <img
                    src={s.image}
                    alt={s.imageCaption ?? s.heading}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                {s.imageCaption && (
                  <figcaption className="mt-3 text-xs uppercase tracking-[0.24em] text-muted-foreground text-center">
                    {s.imageCaption}
                  </figcaption>
                )}
              </figure>
            )}
          </section>
        ))}
      </div>

      {/* CTA */}
      <section
        className="text-bone py-20 md:py-28 text-center"
        style={{ backgroundColor: "oklch(0.18 0.045 40)" }}
      >
        <div className="container-luxe max-w-2xl">
          <div className="eyebrow mb-4 text-gold">From the Maison</div>
          <h3 className="font-serif text-3xl md:text-4xl">
            Leather worth <span className="italic text-gradient-gold text-slate-50">writing about.</span>
          </h3>
          <p className="mt-5 text-bone/90">Discover the pieces behind the words.</p>
          <Link
            to="/shop"
            className="btn-gold mt-8 inline-flex items-center justify-center px-8 py-3 text-xs uppercase tracking-[0.24em]"
          >
            Visit the Collection
          </Link>
        </div>
      </section>

      {/* MORE READS */}
      <section className="container-luxe py-24 md:py-32">
        <div className="eyebrow mb-3 text-gold">Continue Reading</div>
        <h2 className="font-serif text-3xl md:text-4xl mb-12">More from the journal.</h2>
        <div className="grid md:grid-cols-3 gap-10">
          {others.map((b) => (
            <Link
              key={b.slug}
              to="/journal/$slug"
              params={{ slug: b.slug }}
              preload="intent"
              className="group block"
            >
              <div className="relative aspect-[4/5] overflow-hidden img-frame mb-5">
                <img
                  src={b.hero}
                  alt={b.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="eyebrow text-gold mb-2">{b.category}</div>
              <h3 className="font-serif text-xl leading-tight group-hover:text-gold transition-colors">
                {b.title}
              </h3>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
